import { showNotification } from '@helpers/notification-helper';
import { changePassword } from '@services/external/auth.api';
import {
  Button,
  Col,
  Form,
  PasswordChecks,
  PasswordStrength,
  Row,
  TextField,
  Typography,
} from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { initialValues, validationSchema } from './profile-password-change.schema';
import { PasswordChangeSC } from './profile-password-change.styled';

const ProfilePasswordChange: React.FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const [, formOptions] = Form.useForm({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (v, h) => {
      try {
        await changePassword(v.oldPassword, v.password);
        showNotification({
          type: 'SUCCESS',
          title: formatMessage({ id: 'password_change.notification_title' }),
          copy: formatMessage({ id: 'password_change.notification_copy' }),
        });
        h.resetForm();
      } catch (error: any) {
        console.log('error', error);
        const message = error.message;
        const messageCode = error.message_code;

        if (messageCode === 'wrong_user_credentials') {
          h.setFieldError('oldPassword', message);
        } else {
          showNotification({ title: message, copy: '', type: 'ERROR' });
        }
      }
    },
  });

  return (
    <PasswordChangeSC>
      <Typography display="h5" className="title">
        Zmena hesla
      </Typography>
      <Form {...formOptions}>
        <Row gutter={[theme.spacings.component.M, 0]}>
          <Col xs={12} md={6}>
            <Form.Field
              name="oldPassword"
              label={formatMessage({ id: 'password_change.old_password' })}
              required
            >
              <TextField
                type={'password'}
                autoComplete="current-password"
                placeholder={formatMessage({ id: 'password_change.placeholder' })}
              />
            </Form.Field>
          </Col>
        </Row>
        <Row gutter={[theme.spacings.component.M, 0]}>
          <Col xs={12} md={6}>
            <Form.Field
              name="password"
              label={formatMessage({ id: 'password_change.new_password' })}
              required
            >
              <TextField
                type={'password'}
                autoComplete="new-password"
                placeholder={formatMessage({ id: 'password_change.placeholder' })}
              />
            </Form.Field>
            <Form.Custom>
              {(f) => <PasswordStrength password={f.values.password} style={{ marginBottom: 8 }} />}
            </Form.Custom>
            <Form.Field
              name="passwordConfirmation"
              label={formatMessage({ id: 'password_change.repeat' })}
              required
            >
              <TextField
                type={'password'}
                autoComplete="new-password"
                placeholder={formatMessage({ id: 'password_change.placeholder' })}
              />
            </Form.Field>
            <Form.Custom>
              {(f) => (
                <Button
                  type="submit"
                  style={{ marginTop: theme.spacings.component.S }}
                  loading={f.isSubmitting}
                >
                  {formatMessage({ id: 'password_change.submit' })}
                </Button>
              )}
            </Form.Custom>
          </Col>
          <Col xs={12} md={6}>
            <Form.Custom>
              {(f) => <PasswordChecks password={f.values.password} style={{ marginTop: 24 }} />}
            </Form.Custom>
          </Col>
        </Row>
      </Form>
    </PasswordChangeSC>
  );
};

export default React.memo(ProfilePasswordChange);
